import { useEffect } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { ChevronLeft, ChevronRight, Download, Printer, X } from 'lucide-react'
import { KIND_LABELS, formatBytes, isPdf } from '../lib/printMaterials'

/**
 * Full-screen preview of one print material with print / download and
 * previous / next paging. Radix Dialog: portaled, focus-trapped, Escape closes.
 */
export default function AssetPreviewDialog({ asset, assets = [], onClose, onNavigate, onPrint, printing }) {
  const open = Boolean(asset)
  const index = asset ? assets.findIndex((a) => a.id === asset.id) : -1
  const hasPrev = index > 0
  const hasNext = index >= 0 && index < assets.length - 1

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => {
      if (event.key === 'ArrowLeft' && hasPrev) onNavigate(assets[index - 1])
      if (event.key === 'ArrowRight' && hasNext) onNavigate(assets[index + 1])
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, hasPrev, hasNext, index, assets, onNavigate])

  const dims = asset?.width && asset?.height ? `${asset.width} × ${asset.height} px` : null
  const pdf = isPdf(asset)

  return (
    <Dialog.Root open={open} onOpenChange={(next) => { if (!next) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[150] bg-havn-green-900/80 backdrop-blur-sm animate-fade-in" />
        <Dialog.Content
          className="fixed inset-2 md:inset-6 z-[150] flex flex-col bg-havn-cream-100 rounded-2xl shadow-2xl outline-none overflow-hidden"
          aria-describedby={undefined}
        >
          <header className="flex items-center gap-3 px-4 md:px-6 py-3 bg-white border-b border-havn-cream-300">
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-[0.16em] text-havn-brass-600 font-medium">
                {asset ? KIND_LABELS[asset.kind] || 'Print material' : ''}
              </p>
              <Dialog.Title className="font-display text-xl md:text-2xl text-havn-green-900 leading-tight truncate">
                {asset?.label || 'Preview'}
              </Dialog.Title>
              <p className="text-xs text-havn-gray-500 truncate">
                {[dims, formatBytes(asset?.size_bytes), asset?.original_filename].filter(Boolean).join(' · ')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {asset && !pdf && (
                <button type="button" className="btn-primary" onClick={() => onPrint(asset)} disabled={Boolean(printing)}>
                  <Printer className="w-4 h-4" /> {printing ? 'Preparing…' : 'Print'}
                </button>
              )}
              {asset && (
                <a className="btn-secondary" href={asset.download_url} download>
                  <Download className="w-4 h-4" /> Download
                </a>
              )}
              <Dialog.Close asChild>
                <button type="button" className="btn-ghost !px-2" aria-label="Close preview">
                  <X className="w-5 h-5" />
                </button>
              </Dialog.Close>
            </div>
          </header>

          <div className="relative flex-1 min-h-0 flex items-center justify-center p-4 md:p-8">
            {asset && (pdf ? (
              <iframe title={asset.label} src={asset.file_url} className="w-full h-full rounded-lg bg-white border border-havn-cream-300" />
            ) : (
              <img
                key={asset.id}
                src={asset.file_url}
                alt={asset.label}
                className="max-w-full max-h-full object-contain rounded-lg shadow-soft bg-white"
              />
            ))}
            {hasPrev && (
              <button
                type="button"
                onClick={() => onNavigate(assets[index - 1])}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white shadow-soft flex items-center justify-center text-havn-green-900"
                aria-label="Previous"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            {hasNext && (
              <button
                type="button"
                onClick={() => onNavigate(assets[index + 1])}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white shadow-soft flex items-center justify-center text-havn-green-900"
                aria-label="Next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>

          {assets.length > 1 && (
            <footer className="px-6 py-2 text-center text-xs text-havn-gray-500 bg-white border-t border-havn-cream-300">
              {index + 1} of {assets.length} · use ← → to move between files
            </footer>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
